import {
  Autocomplete,
  TextField,
  darken,
  lighten,
  styled,
} from "@mui/material";

import React from "react";

export interface AutoCompleteCustomProps {
  hiddenLabel?: boolean;
  title?: string;
  field?: string;
  fieldId?: any;
  groupByField?: string;
  required?: boolean;
  error?: boolean | string | null;
  options: readonly any[];
  value: any;
  onChange: (selected: any) => void;
  size?: "small" | "medium";
  variant?: "filled" | "outlined" | "standard";
  startAdornment?: React.ReactNode;
  disabled?: boolean;
  clearDisabled?: boolean | undefined;
}

const AutoCompleteCustom = (props: AutoCompleteCustomProps) => {
  const options = props?.options?.map((option) => {
    const firstLetter =
      option[props?.field ?? "name"]?.[0]?.toUpperCase() ?? "";

    return {
      firstLetter: /[0-9]/?.test(firstLetter) ? "0-9" : firstLetter,

      ...option,
    };
  });

  const GroupHeader = styled("div")(({ theme }) => ({
    position: "sticky",

    top: "-8px",

    padding: "4px 10px",

    color: theme.palette.primary.main,

    backgroundColor:
      theme.palette.mode === "light"
        ? lighten(theme.palette.primary.light, 0.85)
        : darken(theme.palette.primary.main, 0.8),
  }));

  const GroupItems = styled("ul")({
    padding: 0,
  });

  const isOptionEqualToValue = (option: any, value: any) => {
    return option[props?.fieldId ?? "id"] === value[props?.fieldId ?? "id"];
  };

  const renderGroup = (params: any) => {
    const groupChildren = Array.isArray(params.children)
      ? params.children
      : [params.children];

    const uniqueChildren: any[] = [];

    const duplicates = new Map();

    // Filtrar os elementos únicos e identificar os duplicados

    groupChildren.forEach((child: any) => {
      const childKey = child.key || child.props.key;

      if (duplicates.has(childKey)) {
        // Se já foi encontrado um elemento com a mesma chave, adicionamos um número na frente

        const duplicateIndex = duplicates.get(childKey) + 1;
        const updatedChild = React.cloneElement(child, {
          key: `${childKey}-${duplicateIndex}`,
        });

        uniqueChildren.push(updatedChild);

        duplicates.set(childKey, duplicateIndex);
      } else {
        // Se é a primeira ocorrência do elemento, adicionamos na lista de únicos

        uniqueChildren.push(child);
        duplicates.set(childKey, 0);
      }
    });

    return (
      <li key={params.key}>
        <GroupHeader>{params.group}</GroupHeader>
        <GroupItems>{uniqueChildren}</GroupItems>
      </li>
    );
  };

  return (
    <Autocomplete
      options={options?.sort(
        (a, b) =>
          -b[props.groupByField ?? "firstLetter"].localeCompare(
            a[props.groupByField ?? "firstLetter"]
          )
      )}
      groupBy={(option) => option[props.groupByField ?? "firstLetter"]}
      getOptionLabel={(option) => option[props.field ?? "name"] ?? ""}
      isOptionEqualToValue={isOptionEqualToValue}
      fullWidth
      autoHighlight
      value={
        options?.find((x) => x[props.fieldId ?? "id"] === props.value) || null
      } // Initialize with null for uncontrolled state
      onChange={(event: any, newValue: any | null) => {
        if (newValue) {
          props.onChange(newValue[props.fieldId ?? "id"]);
        } else {
          props.onChange(null);
        }
      }}
      disableClearable={props.clearDisabled ?? false}
      noOptionsText={"Não encontrado..."}
      renderInput={(params) => (
        <TextField
          {...params}
          hiddenLabel={props.hiddenLabel}
          required={props.required}
          variant={props.variant}
          size={props.size}
          label={props.title}
          error={!!props.error}
          helperText={props.error || ""}
          InputProps={{
            ...params.InputProps,
            startAdornment: props.startAdornment,
          }}
        />
      )}
      renderGroup={renderGroup}
      disabled={props.disabled}
    />
  );
};

export default AutoCompleteCustom;
